/**
 * 지도·표 공용 "이름으로 찾기" — 낱말이 글에 들어 있으면 일치, 일치한 글자는 노란 배경(분류사례의 본문 일치와 같은 표시).
 * 찾기 칸은 카드 머리(지도·표 탭 옆)에 하나 있고, Enter/Shift+Enter 는 `FIND_STEP_EVENT` 로 보이는 쪽에 전달된다.
 */
import React, { useEffect } from "react"; // eslint-disable-line no-unused-vars

export const FIND_STEP_EVENT = "loginhs:find-step";

export function textMatches(text, find) {
  const f = String(find || "").trim().toLowerCase();
  return Boolean(f) && Boolean(text) && String(text).toLowerCase().includes(f);
}

/** 검색어 낱말 목록 정리 — 2자 이상, 중복 제거, 긴 것부터(긴 낱말이 짧은 낱말을 품을 때 한 덩어리로). */
function uniqueNeedles(terms) {
  const out = [];
  for (const t of Array.isArray(terms) ? terms : []) {
    const n = String(t || "").trim().toLowerCase();
    if (n.length >= 2 && !out.includes(n)) out.push(n);
  }
  return out.sort((a, b) => b.length - a.length);
}

/** 검색어 낱말 강조(44차, 2026-09-15 사용자 "키워드가 노란색 반전이면 눈에 띌 것") — 겹치는 구간은 합쳐 한 덩어리로. */
function markTerms(s, needles, keyBase) {
  if (!needles.length || !s) return [s];
  const lower = s.toLowerCase(); const ranges = [];
  for (const n of needles) { let p = 0; while ((p = lower.indexOf(n, p)) !== -1) { ranges.push([p, p + n.length]); p += n.length; } }
  if (!ranges.length) return [s];
  ranges.sort((a, b) => a[0] - b[0]);
  const merged = [];
  for (const r of ranges) { const last = merged[merged.length - 1]; if (last && last[1] >= r[0]) last[1] = Math.max(last[1], r[1]); else merged.push([r[0], r[1]]); }
  const out = []; let i = 0;
  merged.forEach(([a, b], k) => {
    if (a > i) out.push(s.slice(i, a));
    out.push(<mark key={`${keyBase}q${k}`} data-testid="map-query-mark" style={{ background: "#fde68a", color: "inherit", padding: "0 1px", borderRadius: 2 }}>{s.slice(a, b)}</mark>);
    i = b;
  });
  if (i < s.length) out.push(s.slice(i));
  return out;
}

/**
 * 이름 표시 — `find`(찾기 칸)는 map-find-mark, `terms`(검색어 낱말·엔진이 맞춘 낱말)는 map-query-mark. 둘 다 노란 배경이고
 * 찾기 구간이 우선한다(찾기 1/N 계산은 찾기만 센다).
 */
/**
 * 띄어쓰기에서만 줄을 끊는 칸(word-break: keep-all)에서 'ㆍ'·'·' 로 이어진 긴 나열(공장ㆍ창고ㆍ부두…)이
 * 한 낱말처럼 통째로 넘어가지 않게, 가운뎃점 뒤에 줄바꿈 자리(<wbr>)를 둔다. 글자는 바꾸지 않는다(복사·찾기 그대로).
 */
function withDotBreaks(nodes) {
  const list = Array.isArray(nodes) ? nodes : [nodes];
  const out = []; let k = 0;
  for (const n of list) {
    if (typeof n !== "string" || !/[\u318d\u00b7\u30fb]/.test(n)) { out.push(n); continue; }
    // \ub4a4\ubcf4\uae30 \uc815\uaddc\uc2dd\uc740 \uc61b \uc0ac\ud30c\ub9ac\uc5d0\uc11c \ubc88\ub4e4 \uc804\uccb4\ub97c \uae68\ub728\ub9ac\ubbc0\ub85c \uc4f0\uc9c0 \uc54a\ub294\ub2e4 \u2014 \uc810\uc744 \uc7a1\uc544 \ub098\ub208 \ub4a4 \uc810 \ub4a4\uc5d0 <wbr>.
    n.split(/([\u318d\u00b7\u30fb])/).forEach((p) => {
      if (!p) return;
      out.push(p);
      if (/^[\u318d\u00b7\u30fb]$/.test(p)) out.push(<wbr key={`wb${k++}`} />);
    });
  }
  return out;
}

export function Marked(props) {
  return withDotBreaks(markedNodes(props));
}

function markedNodes({ text, find, terms = [] }) {
  const s = String(text || ""); const f = String(find || "").trim();
  const needles = uniqueNeedles(terms).filter((n) => !f || n !== f.toLowerCase());
  if (!f) return needles.length ? markTerms(s, needles, "m") : s;
  const lower = s.toLowerCase(); const fl = f.toLowerCase(); const out = []; let i = 0; let k = 0;
  while (i < s.length) {
    const j = lower.indexOf(fl, i);
    if (j < 0) { out.push(...markTerms(s.slice(i), needles, `t${k}`)); break; }
    if (j > i) out.push(...markTerms(s.slice(i, j), needles, `t${k}`));
    out.push(<mark key={k++} data-testid="map-find-mark" style={{ background: "#fde68a", color: "inherit", padding: "0 1px", borderRadius: 2 }}>{s.slice(j, j + f.length)}</mark>);
    i = j + f.length;
  }
  return out;
}

/** 카드의 찾기 칸에서 Enter(다음)·Shift+Enter(이전)를 보낸다. */
export function requestFindStep(delta) {
  try { window.dispatchEvent(new CustomEvent(FIND_STEP_EVENT, { detail: { delta } })); } catch (_) { /* ignore */ }
}
export function useFindStep(handler) {
  useEffect(() => {
    const on = (e) => { if (handler) handler(Number(e.detail?.delta) || 1); };
    window.addEventListener(FIND_STEP_EVENT, on);
    return () => window.removeEventListener(FIND_STEP_EVENT, on);
  }, [handler]);
}
